import type { UserBase } from '@core/account-types';
import type { MailboxMessageAttachmentAssignment } from '@core/attachments-types';
import type { Candidate } from '@core/candidate-types';
import type { SharedEmailProviderConnectionStatus } from '@core/email-providers-types';
import type { OfferBrief } from '@core/offers-types';
import type { Visibility } from '@core/visibility-types';

import type { MailboxSearchScope } from './mailbox.enums';

export type MailboxInboxPersonal = {
  id: number;
  email: string;
  shared: false;
  references: [UserBase];
  signature: string;
  name: string;
  provider: string;
};

export type MailboxInboxShared = {
  id: number;
  email: string;
  connectionStatus: SharedEmailProviderConnectionStatus;
  shared: true;
  signature: string;
  name: string;
  provider: string;
};

export type MailboxInbox = MailboxInboxPersonal | MailboxInboxShared;

export type MailboxCounters = {
  sent: number;
  scheduled: number;
  notConverted: number;
  notDelivered: number;
  my: number;
  jobs: number;
  general: number;
  followedJobs: number;
  followedCandidates: number;
  followed: number;
  candidates: number;
  archive: number;
};

export type MailboxSettings = {
  catchAllAddress: string;
  domain: string;
  protectedDomains: string[];
};

export type MailboxProtectedDomains = { protectedDomains: string[]; candidateCount?: number };

export type MailboxRecipient = {
  name?: string;
  email: string;
  emailsFromThread?: string[];
  candidateId?: number;
  candidate?: Candidate;
  emailProviderId?: number;
  adminId?: number | null;
  admin?: UserBase;
};

export type MailboxMessage = {
  id: number;
  threadId: number;
  to: MailboxRecipient[];
  from: MailboxRecipient[];
  cc: MailboxRecipient[];
  bcc: MailboxRecipient[];
  subject: string;
  safeStrippedHtml: string;
  safeHtml: string;
  strippedHtml: string;
  strippedText: string;
  stripped: boolean;
  timestamp: string;
  text: string;
  triggered: boolean;
  deliveryStatus: string;
  deliveryError?: {
    message: string;
    details?: string;
  };
  attachmentAssignments?: MailboxMessageAttachmentAssignment[];
  sendAt?: string;
  sendAtTimezone?: string;
};

export type MailboxThread = {
  id: number;
  archivedAt: string;
  deletedAt: string;
  deliveryStatus: string;
  lastMessageExcerpt: string;
  lastMessageAt: string;
  lastAdminMessageAt: string;
  subject: string;
  from: {
    email: string;
    name: string;
  };
  candidateId: number | null;
  candidate: Candidate | null;
  messages: MailboxMessage[];
  offerId: number | null;
  offer: OfferBrief | null;
  talentPoolId: number | null;
  read: boolean;
  relatedIds: number[];
  related: MailboxThread[];
  sendAt?: string;
  visibility: Visibility;
  isAnonymous?: boolean;
};

export type RecipientEmailObject = {
  email: string;
  name: string;
  emailProviderId?: number;
  admin?: any;
  candidate?: any;
  candidateId?: number;
};

export enum RecipientTypeheadKind {
  All = 'all',
  Candidate = 'Candidate',
  Admin = 'Admin',
  External = 'External',
}

export enum RecipientTypeaheadField {
  To = 'to',
  CC = 'carbonCopy',
}

export type RecipientTypehead = {
  id: string;
  kind: RecipientTypeheadKind;
  value: string;
  emailObject: RecipientEmailObject;
};

export type MailboxNewMessageData = {
  attachments: number[];
  to: MailboxRecipient[];
  cc: MailboxRecipient[];
  bcc: MailboxRecipient[];
  bodyHtml: string;
  emailProviderId: number;
  replyToMessageId: number;
  subject: string;
  sendAt?: string;
  sendAtTimezone?: string;
};

export type MailboxEditedMessageData = {
  to: Array<{
    candidateId?: number;
    candidateEmail?: string;
    emailProviderId?: number;
    email?: string;
  }>;
  attachments: number[];
  bodyHtml: string;
  sendAtTimezone: string;
  sendAt: string;
  subject: string;
};

export type MailboxSearchProviderParams =
  | {
      scope_data: 'all';
    }
  | {
      'scope_data[email_provider_id]': number;
    };

export type MailboxSearchParams = {
  limit: number;
  page: number;
  q: string;
  read: boolean;
  scope: MailboxSearchScope;
} & MailboxSearchProviderParams;

export type MailboxConvertData = {
  data: {
    photo: string | null;
    name: string;
    cv: string | null;
    coverLetter: string;
    coverLetterFile: string | null;
    emails: string[];
    phones: string[];
    links: string[];
    socialLinks: string[];
    customFields: string[];
  };
  offers: Array<{ id: number; locationIds: number[] | null }> | null;
  talentPools: Array<{ id: number }> | null;
  formData?: boolean;
  mailboxThreadId: number | null;
  attachmentAssignmentId: number | null;
};

// TODO: replace this type with MailboxInboxSelectValue & Item
export type SharedEmailProviderItemValue = {
  type: any;
  id: number;
  provider: MailboxInbox;
};

export type SharedEmailProviderItem = {
  label: string;
  value: SharedEmailProviderItemValue;
};
